import { Fragment, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import styles from './AdminPrintOrdersPage.module.css';

interface PrintOrderLineItem {
  filename: string;
  size: string;
  finish: string;
  /** Absent on orders placed before the border option existed. */
  border?: boolean;
  quantity: number;
}

/** Human-readable finish label (`fine_matte` → `Fine Matte`). */
function finishLabel(finish: string): string {
  return finish
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

/** Reap threshold in milliseconds (7 days). */
const REAP_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000;

/** Returns true if the order is abandoned: DRAFT or PENDING_PAYMENT and older than 7 days. */
function isAbandoned(order: PrintOrder): boolean {
  if (order.status !== 'DRAFT' && order.status !== 'PENDING_PAYMENT') return false;
  const age = Date.now() - new Date(order.created_at).getTime();
  return age >= REAP_THRESHOLD_MS;
}

/**
 * Can an admin start (or restart) fulfilment for this order?
 *
 * Gating this on `fulfilment_failed` alone left a paid order that never started
 * fulfilment with no action at all: no ZIP to download, and no button to produce
 * one. That is exactly the state a payment reconciled late lands in, so the real
 * question is "has this been paid for, and is there still no ZIP?".
 *
 * FULFILLING is excluded deliberately — the workflow is already running, and the
 * regenerate endpoint names each execution uniquely, so a second click would
 * start a duplicate rather than being rejected as a repeat.
 */
export function canRegenerate(order: PrintOrder): boolean {
  if (order.fulfilment_failed) return true;
  if (order.download_url) return false;
  return order.status === 'PAID' || order.status === 'FULFILLED' || order.status === 'FAILED';
}

interface PrintOrder {
  print_order_id: string;
  shopify_order_number: string;
  status: string;
  created_at: string;
  download_url?: string;
  fulfilment_failed?: boolean;
  line_items: PrintOrderLineItem[];
}

export function AdminPrintOrdersPage() {
  usePageTitle('Admin — Print orders');
  const { handleUnauthorized, logout } = useAdminAuth();
  const queryClient = useQueryClient();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 1500);
    } catch {
      // Clipboard unavailable (e.g. insecure context) — silently no-op.
    }
  };

  const { data: printOrders, isLoading, error } = useQuery<PrintOrder[]>({
    queryKey: ['admin', 'print-orders'],
    queryFn: async () => {
      const res = await fetch('/api/v1/admin/print-orders', { credentials: 'include' });
      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        throw new Error('Unauthorized');
      }
      if (!res.ok) throw new Error('Failed to fetch print orders');
      const data = await res.json();
      return data.print_orders;
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: async (printOrderId: string) => {
      const res = await fetch(`/api/v1/admin/print-orders/${printOrderId}/regenerate`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        throw new Error('Unauthorized');
      }
      if (!res.ok) throw new Error('Failed to regenerate');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'print-orders'] });
    },
  });

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <main className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.heading}>Admin — Print Orders</h1>
        <div className={styles.headerActions}>
          <Link to="/admin/orders" className={styles.secondaryBtn}>Orders</Link>
          <button onClick={logout} className={styles.secondaryBtn}>Logout</button>
        </div>
      </div>

      {isLoading && <p className={styles.loading}>Loading print orders...</p>}
      {error && <p className={styles.error}>Error loading print orders.</p>}

      {printOrders && printOrders.length > 0 && (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th></th>
                <th>Print Order ID</th>
                <th>Shopify Order #</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {printOrders.map((po) => (
                <Fragment key={po.print_order_id}>
                  <tr>
                    <td>
                      {po.line_items && po.line_items.length > 0 && (
                        <button
                          onClick={() => toggleRow(po.print_order_id)}
                          className={styles.expandBtn}
                          aria-label={expandedRows.has(po.print_order_id) ? 'Collapse' : 'Expand'}
                        >
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"
                            style={{ transform: expandedRows.has(po.print_order_id) ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}
                          >
                            <path d="M2 1L8 5L2 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      )}
                    </td>
                    <td>
                      <div className={styles.idCell}>
                        <span className={styles.printOrderId} title={po.print_order_id}>
                          {po.print_order_id.slice(0, 8)}…
                        </span>
                        <button
                          type="button"
                          className={styles.copyBtn}
                          onClick={() => copyId(po.print_order_id)}
                          aria-label={`Copy full print order ID ${po.print_order_id}`}
                        >
                          {copiedId === po.print_order_id ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    </td>
                    <td>{po.shopify_order_number}</td>
                    <td>
                      {po.status}
                      {isAbandoned(po) && (
                        <span className={styles.abandonedBadge}>abandoned (pending reap)</span>
                      )}
                    </td>
                    <td>{new Date(po.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className={styles.actionsCell}>
                        {po.download_url && (
                          <a
                            href={po.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.primaryBtn}
                          >
                            Download ZIP
                          </a>
                        )}
                        {canRegenerate(po) && (
                          <button
                            onClick={() => regenerateMutation.mutate(po.print_order_id)}
                            disabled={regenerateMutation.isPending}
                            className={styles.secondaryBtn}
                          >
                            {po.download_url || po.fulfilment_failed
                              ? 'Regenerate'
                              : 'Start fulfilment'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedRows.has(po.print_order_id) && po.line_items && (
                    <tr>
                      <td colSpan={6} className={styles.expandedRow}>
                        <table className={styles.lineItemsTable}>
                          <thead>
                            <tr>
                              <th>Filename</th>
                              <th>Size</th>
                              <th>Finish</th>
                              <th>Border</th>
                              <th>Quantity</th>
                            </tr>
                          </thead>
                          <tbody>
                            {po.line_items.map((item, idx) => (
                              <tr key={idx}>
                                <td>{item.filename}</td>
                                <td>{item.size}</td>
                                <td>{item.finish ? finishLabel(item.finish) : '—'}</td>
                                <td>{item.border ? '5mm white' : 'Borderless'}</td>
                                <td>{item.quantity}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {printOrders && printOrders.length === 0 && <p className={styles.empty}>No print orders found.</p>}
    </main>
  );
}
